import React from "react";
import { Link } from "react-router-dom";
import Icon, { IconName } from "@/components/ui/Icon";
import styles from "./styles.module.scss";

type Variant = "primary" | "secondary" | "link" | "danger" | "confirmed";
type Size = "sm" | "md" | "lg";

interface BaseProps {
  variant?: Variant;
  size?: Size;
  icon?: IconName;
  iconRight?: IconName;
  block?: boolean;
  loading?: boolean;
  className?: string;
  children?: React.ReactNode;
}

type ButtonAsButton = BaseProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "className"> & {
    to?: undefined;
    href?: undefined;
  };

type ButtonAsLink = BaseProps & {
  to: string;
  href?: undefined;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

type ButtonAsAnchor = BaseProps & {
  href: string;
  to?: undefined;
  target?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

export type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor;

const Button: React.FC<ButtonProps> = (props) => {
  const {
    variant = "primary",
    size = "md",
    icon,
    iconRight,
    block,
    loading,
    className,
    children,
  } = props;

  const classes = [
    styles.button,
    styles[variant],
    styles[size],
    block ? styles.block : "",
    loading ? styles.loading : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  const iconSize = size === "lg" ? 18 : 16;
  const content = (
    <>
      {icon && <Icon name={icon} size={iconSize} strokeWidth={2.2} />}
      {children}
      {iconRight && <Icon name={iconRight} size={iconSize} strokeWidth={2.2} />}
    </>
  );

  if (props.to !== undefined) {
    return (
      <Link to={props.to} className={classes} onClick={props.onClick}>
        {content}
      </Link>
    );
  }

  if (props.href !== undefined) {
    const external = props.target === "_blank";
    return (
      <a
        href={props.href}
        target={props.target}
        rel={external ? "noopener noreferrer" : undefined}
        className={classes}
        onClick={props.onClick}
      >
        {content}
      </a>
    );
  }

  const {
    variant: _v,
    size: _s,
    icon: _i,
    iconRight: _ir,
    block: _b,
    loading: _l,
    className: _c,
    children: _ch,
    type = "button",
    disabled,
    ...rest
  } = props as ButtonAsButton;

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {content}
    </button>
  );
};

export default Button;
